import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useSortedPaginated, SortableTh, Pagination } from '../components/TableControls';
import { formatMonthYear } from '../utils/dateUtils';
import InvalidateWarningModal from '../components/InvalidateWarningModal';

export default function CountEntry() {
  const { user } = useAuth();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState('');
  const [calculators, setCalculators] = useState({});
  // Saved per-item breakdowns loaded from the server; used to repopulate the
  // calculator when an item is reopened via the # button.
  const [savedCalcs, setSavedCalcs] = useState({});
  // Once the user confirms the strong warning, send force on all later saves so the
  // modal doesn't reappear per cell (counts autosave on blur).
  const [forceAck, setForceAck] = useState(false);
  const [invalidate, setInvalidate] = useState(null);

  const loadSession = useCallback(() => {
    api.get(`/counting/${sessionId}`).then(r => {
      setSession(r.data);
      const map = {};
      const savedCalcs = {};
      r.data.entries.forEach(e => {
        map[e.item_id] = { quantity: e.counted_quantity, notes: e.notes || '' };
        if (
          e.calc_containers != null || e.calc_units_per_container != null ||
          e.calc_in_storage != null || e.calc_on_display != null
        ) {
          savedCalcs[e.item_id] = {
            containers: e.calc_containers ?? '',
            unitsPerContainer: e.calc_units_per_container ?? '',
            inStorage: e.calc_in_storage ?? '',
            onDisplay: e.calc_on_display ?? '',
          };
        }
      });
      setCounts(map);
      // Remember the saved breakdown for each item so reopening its calculator
      // (the # button) repopulates the fields the user originally entered.
      setSavedCalcs(savedCalcs);
    });
  }, [sessionId]);

  useEffect(() => {
    loadSession();
    api.get('/items').then(r => setItems(r.data));
  }, [loadSession]);

  const filtered = useMemo(() =>
    items.filter(i => i.name.toLowerCase().includes(search.toLowerCase())),
    [items, search]
  );

  const { paginated, sortKey, sortDir, setSort, page, setPage, perPage, setPerPage, total } = useSortedPaginated(filtered, 'name');

  const saveEntry = async (itemId, force) => {
    const entry = counts[itemId];
    if (!entry || entry.quantity === '' || entry.quantity === undefined) return;
    const useForce = force ?? forceAck;
    try {
      await api.post(`/counting/${sessionId}/entries${useForce ? '?force=true' : ''}`, {
        item_id: itemId,
        counted_quantity: parseInt(entry.quantity, 10),
        notes: entry.notes || null,
      });
      loadSession();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => { setForceAck(true); saveEntry(itemId, true); } });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error al guardar');
    }
  };

  const updateCount = (itemId, field, value) => {
    setCounts(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value },
    }));
  };

  const toggleCalc = (itemId) => {
    setCalculators(prev => ({
      ...prev,
      [itemId]: prev[itemId]
        ? undefined
        : (savedCalcs[itemId] || { containers: '', unitsPerContainer: '', inStorage: '', onDisplay: '' }),
    }));
  };

  const updateCalc = (itemId, field, value) => {
    setCalculators(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value },
    }));
  };

  const applyCalc = (itemId, force) => {
    const calc = calculators[itemId];
    if (!calc) return;
    const useForce = force ?? forceAck;
    const toInt = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));
    const containers = toInt(calc.containers);
    const unitsPerContainer = toInt(calc.unitsPerContainer);
    const inStorage = toInt(calc.inStorage);
    const onDisplay = toInt(calc.onDisplay);
    const total = (containers || 0) * (unitsPerContainer || 0) + (inStorage || 0) + (onDisplay || 0);
    updateCount(itemId, 'quantity', total);
    const notes = counts[itemId]?.notes || null;
    api.post(`/counting/${sessionId}/entries${useForce ? '?force=true' : ''}`, {
      item_id: itemId,
      counted_quantity: total,
      notes,
      calc_containers: containers,
      calc_units_per_container: unitsPerContainer,
      calc_in_storage: inStorage,
      calc_on_display: onDisplay,
    }).then(() => loadSession()).catch(err => {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => { setForceAck(true); applyCalc(itemId, true); } });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error al guardar');
    });
  };

  if (!session) return <p>Cargando...</p>;

  const monthFinalized = session.month_finalized;
  const readOnly = monthFinalized && user?.role !== 'admin';

  return (
    <div>
      <div className="page-header">
        <h1>Conteo: {session.store_name} - {formatMonthYear(session.month_year)}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => navigate('/counting')}>Volver</button>
        </div>
      </div>

      {monthFinalized && (
        <div className="alert alert-warning">
          {readOnly
            ? 'Este mes tiene una conciliación finalizada. El conteo es de solo lectura.'
            : '⚠️ Este mes tiene una conciliación FINALIZADA. Cualquier cambio en el conteo la invalidará y reabrirá el mes.'}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <span style={{ color: 'var(--text-2)', fontSize: 13 }}>
          {Object.keys(counts).length} de {items.length} artículos contados
        </span>
      </div>

      {msg && <div className={`alert ${msg.includes('Error') ? 'alert-danger' : 'alert-success'}`}>{msg}</div>}

      <input
        placeholder="Buscar artículos..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      <div className="card">
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortableTh colKey="name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Artículo</SortableTh>
                <SortableTh colKey="category" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Categoría</SortableTh>
                <th style={{ width: 140 }}>Conteo</th>
                <th style={{ width: 60 }}>Calc.</th>
                <th style={{ width: 200 }}>Notas</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(item => {
                const entry = counts[item.id] || {};
                const calc = calculators[item.id];
                return (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>{item.name}</td>
                      <td>{item.category || '-'}</td>
                      <td>
                        <input
                          type="number" step="1" min="0"
                          value={entry.quantity ?? ''}
                          onChange={e => updateCount(item.id, 'quantity', e.target.value)}
                          onBlur={() => saveEntry(item.id)}
                          disabled={readOnly}
                          style={{ width: 120 }}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => toggleCalc(item.id)}
                          disabled={readOnly}
                          title="Calculadora"
                        >
                          #
                        </button>
                      </td>
                      <td>
                        <input
                          value={entry.notes || ''}
                          onChange={e => updateCount(item.id, 'notes', e.target.value)}
                          onBlur={() => saveEntry(item.id)}
                          disabled={readOnly}
                          placeholder="Notas..."
                          style={{ width: '100%' }}
                        />
                      </td>
                    </tr>
                    {calc && (
                      <tr>
                        <td colSpan={5}>
                          <div className="calculator-widget">
                            <div className="calc-row">
                              <div className="calc-field">
                                <input
                                  type="number" step="1" min="0"
                                  value={calc.containers}
                                  onChange={e => updateCalc(item.id, 'containers', e.target.value)}
                                />
                                <span className="calc-label">Envases</span>
                              </div>
                              <span className="calc-op">×</span>
                              <div className="calc-field">
                                <input
                                  type="number" step="1" min="0"
                                  value={calc.unitsPerContainer}
                                  onChange={e => updateCalc(item.id, 'unitsPerContainer', e.target.value)}
                                />
                                <span className="calc-label">Unidades / envase</span>
                              </div>
                              <span className="calc-op">+</span>
                              <div className="calc-field">
                                <input
                                  type="number" step="1" min="0"
                                  value={calc.inStorage}
                                  onChange={e => updateCalc(item.id, 'inStorage', e.target.value)}
                                />
                                <span className="calc-label">Unidades en bodega</span>
                              </div>
                              <span className="calc-op">+</span>
                              <div className="calc-field">
                                <input
                                  type="number" step="1" min="0"
                                  value={calc.onDisplay}
                                  onChange={e => updateCalc(item.id, 'onDisplay', e.target.value)}
                                />
                                <span className="calc-label">Unidades en exhibición</span>
                              </div>
                              <span className="calc-op">=</span>
                              <div className="calc-field">
                                <span className="calc-result">
                                  {(parseInt(calc.containers, 10) || 0) * (parseInt(calc.unitsPerContainer, 10) || 0) + (parseInt(calc.inStorage, 10) || 0) + (parseInt(calc.onDisplay, 10) || 0)}
                                </span>
                                <span className="calc-label">Total</span>
                              </div>
                              <button className="btn btn-sm btn-success" onClick={() => applyCalc(item.id)}>Aplicar</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
      </div>

      <InvalidateWarningModal
        data={invalidate}
        onCancel={() => setInvalidate(null)}
        onConfirm={() => { const r = invalidate.run; setInvalidate(null); r(); }}
      />
    </div>
  );
}
